import React from "react";
import { Graphics } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";

/**
 * PopiScrollbar - A scrollbar component with gray background and light gray thumb
 * Features wider width than PixiScrollbar, gray track with light gray thumb
 *
 * Props:
 * - scrollY: Current scroll position (number)
 * - maxScrollY: Maximum scrollable distance (number)
 * - viewportHeight: Height of visible area (number)
 * - x: X position for the scrollbar (number)
 * - y: Y position for the scrollbar (number)
 */
const Scrollbar = ({ scrollY, maxScrollY, viewportHeight, x, y }) => {
  const { getResponsiveConfig } = useDimensions();
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);
  if (maxScrollY <= 0) {
    return null;
  } // Don't show scrollbar if no scrolling needed

  // Use divider width for scalable scrollbar width, but make it wider (2x)
  const baseWidth = buttonConfig.width * 0.2;
  const scrollbarWidth = baseWidth; // Wider than PixiScrollbar
  const scrollbarHeight = viewportHeight;

  // Calculate thumb size and position
  const contentRatio = viewportHeight / (viewportHeight + maxScrollY);
  const thumbHeight = Math.max(
    scrollbarHeight * contentRatio,
    baseWidth * 3 // Minimum thumb height = 2x base width
  );
  const scrollRatio = scrollY / maxScrollY;
  const thumbY = y + (scrollbarHeight - thumbHeight) * scrollRatio;

  // Gray colors
  const trackColor = 0x2a2a2a; // Darker gray background
  const thumbColor = 0x9a9a9a; // Light gray thumb

  return (
    <Graphics
      x={x}
      y={y}
      draw={(g) => {
        g.clear();

        // Scrollbar track (background) - dark gray
        g.beginFill(trackColor, 1);
        g.drawRoundedRect(
          0,
          0,
          scrollbarWidth,
          scrollbarHeight,
          0 // Square corners
        );
        g.endFill();

        // Scrollbar thumb (handle) - light gray with rounded corners
        g.beginFill(thumbColor, 1);
        const borderRadius = Math.min(scrollbarWidth / 2, thumbHeight / 2); // Radius based on width or height, whichever is smaller
        g.drawRoundedRect(
          0,
          thumbY - y,
          scrollbarWidth,
          thumbHeight,
          borderRadius // Rounded corners
        );
        g.endFill();
      }}
    />
  );
};

export default Scrollbar;
